import { WA_PHYSICS } from '../core/constants';
import { SeededRNG, getGameRNG } from '../core/rng';
import type { TerrainDrawContext } from '../sim/maskCanvas';
import { initNoise, fractalNoise2D, simplex2D } from './noise';
import { FeaturePlacer } from './featurePlacer';
import {
  generateCaveMap,
  applyCaveToCanvas,
  addFormations,
  type CaveOptions,
} from './caveGenerator';

export type TerrainTheme = 'dirt' | 'snow' | 'hell' | 'jungle' | 'desert' | 'alien';
export type MapComplexity = 'simple' | 'normal' | 'complex';
export type MapStyle =
  | 'random'
  | 'island'
  | 'bridges'
  | 'towers'
  | 'fortress'
  | 'tunnels'
  | 'platforms'
  | 'cavern';

export interface MapGeneratorOptions {
  theme?: TerrainTheme;
  complexity?: MapComplexity;
  mapStyle?: MapStyle;
  floatingIslands?: boolean;
  floatingIslandCount?: { min: number; max: number };
  bridges?: boolean;
  pillars?: boolean;
  caves?: boolean;
  girders?: boolean;
}

interface ThemeColors {
  ground: string;
  surface: string;
  rock: string;
  accent: string;
}

const THEME_COLORS: Record<TerrainTheme, ThemeColors> = {
  dirt: {
    ground: '#8B4513',
    surface: '#228B22',
    rock: '#654321',
    accent: '#4a3520',
  },
  snow: {
    ground: '#E8E8E8',
    surface: '#FFFFFF',
    rock: '#B0C4DE',
    accent: '#87CEEB',
  },
  hell: {
    ground: '#8B0000',
    surface: '#FF4500',
    rock: '#4B0000',
    accent: '#FF6347',
  },
  jungle: {
    ground: '#2E8B57',
    surface: '#00FF7F',
    rock: '#006400',
    accent: '#32CD32',
  },
  desert: {
    ground: '#DEB887',
    surface: '#F4A460',
    rock: '#D2691E',
    accent: '#CD853F',
  },
  alien: {
    ground: '#4B0082',
    surface: '#9400D3',
    rock: '#2E0854',
    accent: '#8A2BE2',
  },
};

export class MapGenerator {
  private rng: SeededRNG;
  private width: number;
  private height: number;
  private waterLevel: number;

  constructor(width: number = WA_PHYSICS.WORLD.WIDTH, height: number = WA_PHYSICS.WORLD.HEIGHT) {
    this.rng = getGameRNG();
    this.width = width;
    this.height = height;
    this.waterLevel = WA_PHYSICS.WORLD.WATER_LEVEL;
  }

  generate(ctx: TerrainDrawContext, options: MapGeneratorOptions = {}): void {
    const {
      theme = 'dirt',
      complexity = 'normal',
      mapStyle = 'random',
      floatingIslands = true,
      floatingIslandCount = { min: 2, max: 5 },
      bridges = true,
      pillars = false,
      caves = false,
      girders = true,
    } = options;

    ctx.clearRect(0, 0, this.width, this.height);

    const colors = THEME_COLORS[theme];

    switch (mapStyle) {
      case 'island':
        this.generateIslandMap(ctx, colors, complexity);
        break;
      case 'bridges':
        this.generateBridgesMap(ctx, colors, complexity);
        break;
      case 'towers':
        this.generateTowersMap(ctx, colors, complexity);
        break;
      case 'fortress':
        this.generateFortressMap(ctx, colors, complexity);
        break;
      case 'tunnels':
        this.generateTunnelsMap(ctx, colors, complexity);
        break;
      case 'platforms':
        this.generatePlatformsMap(ctx, colors, complexity);
        break;
      case 'cavern':
        this.generateCavernMap(ctx, colors, complexity, girders);
        break;
      default:
        this.generateRandomMap(ctx, colors, complexity, {
          floatingIslands,
          floatingIslandCount,
          bridges,
          pillars,
          caves,
          girders,
        });
    }

    this.drawWater(ctx);
  }

  private generateRandomMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
    options: {
      floatingIslands: boolean;
      floatingIslandCount: { min: number; max: number };
      bridges: boolean;
      pillars: boolean;
      caves: boolean;
      girders: boolean;
    },
  ): void {
    const heightMap = this.generateSmoothHeightMap(complexity);

    this.drawMainTerrain(ctx, heightMap, colors);

    if (options.caves) {
      this.carveRandomCaves(ctx, heightMap);
    }

    if (options.pillars) {
      this.addPillars(ctx, heightMap, colors);
    }

    if (options.bridges) {
      this.addNaturalBridges(ctx, heightMap, colors);
    }

    if (options.floatingIslands) {
      const count = this.rng.nextInt(
        options.floatingIslandCount.min,
        options.floatingIslandCount.max,
      );
      this.addFloatingIslands(ctx, heightMap, colors, count);
    }

    if (options.girders) {
      this.addGirders(ctx, heightMap);
    }

    this.addTerrainTexture(ctx, colors);
  }

  private generateSmoothHeightMap(complexity: MapComplexity): number[] {
    initNoise(this.rng);

    const heightMap: number[] = [];
    const baseY = this.height * 0.55;

    const octaves = complexity === 'simple' ? 3 : complexity === 'normal' ? 4 : 6;
    const amplitude =
      this.height * (complexity === 'simple' ? 0.08 : complexity === 'normal' ? 0.12 : 0.18);
    const frequency = 0.003;
    const largeFeatureFreq = 0.0008;

    for (let x = 0; x <= this.width; x++) {
      const mainNoise = fractalNoise2D(x * frequency, 0, octaves, 2.0, 0.5);
      const largeFeatures = fractalNoise2D(x * largeFeatureFreq, 100, 2, 2.0, 0.5);

      let y = baseY + mainNoise * amplitude + largeFeatures * amplitude * 0.6;

      y = Math.max(this.height * 0.15, Math.min(y, this.height - this.waterLevel - 40));
      heightMap.push(y);
    }

    return this.smoothHeightMap(heightMap, 4);
  }

  private generateIslandMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
  ): void {
    const islandCount = complexity === 'simple' ? 2 : complexity === 'normal' ? 3 : 4;
    const spacing = this.width / (islandCount + 1);

    for (let i = 0; i < islandCount; i++) {
      const cx = spacing * (i + 1) + (this.rng.next() - 0.5) * spacing * 0.3;
      const islandWidth = 120 + this.rng.next() * 100;
      const islandHeight = 150 + this.rng.next() * 100;
      const baseY = this.height * 0.5 + this.rng.next() * 50;

      this.drawLargeIsland(ctx, cx, baseY, islandWidth, islandHeight, colors);
    }

    const smallIslandCount = complexity === 'simple' ? 1 : complexity === 'normal' ? 2 : 4;
    for (let i = 0; i < smallIslandCount; i++) {
      const cx = this.rng.next() * this.width * 0.8 + this.width * 0.1;
      const cy = this.height * 0.2 + this.rng.next() * this.height * 0.25;
      const width = 60 + this.rng.next() * 60;
      const height = 30 + this.rng.next() * 30;
      this.drawIsland(ctx, cx, cy, width, height, colors);
    }

    this.addGirders(ctx, new Array(this.width).fill(this.height));
    this.addTerrainTexture(ctx, colors);
  }

  private generateBridgesMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
  ): void {
    const platformCount = complexity === 'simple' ? 3 : complexity === 'normal' ? 4 : 5;
    const platformY = this.height * 0.6;
    const platformWidth = 100;
    const spacing = this.width / (platformCount + 1);

    const platforms: { x: number; y: number; width: number }[] = [];

    for (let i = 0; i < platformCount; i++) {
      const x = spacing * (i + 1);
      const y = platformY + (this.rng.next() - 0.5) * 60;
      const w = platformWidth + (this.rng.next() - 0.5) * 40;

      ctx.fillStyle = colors.ground;
      ctx.beginPath();
      ctx.moveTo(x - w / 2, y);
      ctx.lineTo(x + w / 2, y);
      ctx.lineTo(x + w / 2 - 20, this.height);
      ctx.lineTo(x - w / 2 + 20, this.height);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = colors.surface;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(x - w / 2, y);
      ctx.lineTo(x + w / 2, y);
      ctx.stroke();

      platforms.push({ x, y, width: w });
    }

    ctx.fillStyle = colors.rock;
    for (let i = 0; i < platforms.length - 1; i++) {
      const p1 = platforms[i];
      const p2 = platforms[i + 1];
      const bridgeY = Math.min(p1.y, p2.y) - 10;
      const thickness = 12;

      ctx.beginPath();
      ctx.moveTo(p1.x + p1.width / 2, bridgeY);
      const midX = (p1.x + p2.x) / 2;
      const sag = 20 + this.rng.next() * 20;
      ctx.quadraticCurveTo(midX, bridgeY + sag, p2.x - p2.width / 2, bridgeY);
      ctx.lineTo(p2.x - p2.width / 2, bridgeY + thickness);
      ctx.quadraticCurveTo(
        midX,
        bridgeY + sag + thickness,
        p1.x + p1.width / 2,
        bridgeY + thickness,
      );
      ctx.closePath();
      ctx.fill();
    }

    const floatingCount = complexity === 'simple' ? 2 : complexity === 'normal' ? 3 : 5;
    for (let i = 0; i < floatingCount; i++) {
      const x = this.rng.next() * this.width * 0.8 + this.width * 0.1;
      const y = this.height * 0.15 + this.rng.next() * this.height * 0.25;
      this.drawIsland(ctx, x, y, 70 + this.rng.next() * 50, 25 + this.rng.next() * 20, colors);
    }

    this.addTerrainTexture(ctx, colors);
  }

  private generateTowersMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
  ): void {
    const baseHeight = 40;
    ctx.fillStyle = colors.ground;
    ctx.fillRect(
      0,
      this.height - this.waterLevel - baseHeight,
      this.width,
      baseHeight + this.waterLevel,
    );
    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, this.height - this.waterLevel - baseHeight);
    ctx.lineTo(this.width, this.height - this.waterLevel - baseHeight);
    ctx.stroke();

    const towerCount = complexity === 'simple' ? 3 : complexity === 'normal' ? 5 : 7;
    const spacing = this.width / (towerCount + 1);

    for (let i = 0; i < towerCount; i++) {
      const x = spacing * (i + 1) + (this.rng.next() - 0.5) * spacing * 0.3;
      const towerWidth = 40 + this.rng.next() * 30;
      const towerHeight = 150 + this.rng.next() * 200;
      const baseY = this.height - this.waterLevel - baseHeight;

      ctx.fillStyle = colors.ground;
      ctx.fillRect(x - towerWidth / 2, baseY - towerHeight, towerWidth, towerHeight);

      ctx.strokeStyle = colors.surface;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x - towerWidth / 2, baseY - towerHeight);
      ctx.lineTo(x + towerWidth / 2, baseY - towerHeight);
      ctx.stroke();

      if (this.rng.next() > 0.5) {
        const ledgeY = baseY - towerHeight * (0.3 + this.rng.next() * 0.4);
        const ledgeWidth = towerWidth * 1.5;
        ctx.fillStyle = colors.rock;
        ctx.fillRect(x - ledgeWidth / 2, ledgeY, ledgeWidth, 10);
      }
    }

    this.addTerrainTexture(ctx, colors);
  }

  private generateFortressMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
  ): void {
    const wallHeight = 200;
    const wallThickness = 50;
    const baseY = this.height - this.waterLevel - 30;

    ctx.fillStyle = colors.ground;
    ctx.fillRect(0, baseY, this.width, 30 + this.waterLevel);
    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, baseY);
    ctx.lineTo(this.width, baseY);
    ctx.stroke();

    ctx.fillStyle = colors.ground;
    ctx.fillRect(0, baseY - wallHeight, wallThickness, wallHeight);
    ctx.fillRect(this.width - wallThickness, baseY - wallHeight, wallThickness, wallHeight);

    const battlementWidth = 15;
    const battlementHeight = 25;
    const battlementSpacing = 25;
    for (let x = 0; x < wallThickness; x += battlementSpacing) {
      ctx.fillRect(x, baseY - wallHeight - battlementHeight, battlementWidth, battlementHeight);
      ctx.fillRect(
        this.width - wallThickness + x,
        baseY - wallHeight - battlementHeight,
        battlementWidth,
        battlementHeight,
      );
    }

    const floorCount = complexity === 'simple' ? 2 : complexity === 'normal' ? 3 : 4;
    const floorSpacing = wallHeight / (floorCount + 1);
    for (let i = 1; i <= floorCount; i++) {
      const floorY = baseY - i * floorSpacing;
      const floorWidth = 80 + this.rng.next() * 40;

      ctx.fillStyle = colors.rock;
      ctx.fillRect(wallThickness - 10, floorY, floorWidth, 12);
      ctx.fillRect(this.width - wallThickness - floorWidth + 10, floorY, floorWidth, 12);
    }

    const centerPlatformY = baseY - wallHeight * 0.6;
    const centerWidth = 150 + this.rng.next() * 100;
    ctx.fillStyle = colors.ground;
    ctx.beginPath();
    ctx.moveTo(this.width / 2 - centerWidth / 2, centerPlatformY);
    ctx.lineTo(this.width / 2 + centerWidth / 2, centerPlatformY);
    ctx.lineTo(this.width / 2 + centerWidth / 2 - 30, centerPlatformY + 80);
    ctx.lineTo(this.width / 2 - centerWidth / 2 + 30, centerPlatformY + 80);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(this.width / 2 - centerWidth / 2, centerPlatformY);
    ctx.lineTo(this.width / 2 + centerWidth / 2, centerPlatformY);
    ctx.stroke();

    this.addTerrainTexture(ctx, colors);
  }

  private generateTunnelsMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
  ): void {
    ctx.fillStyle = colors.ground;
    ctx.fillRect(0, this.height * 0.25, this.width, this.height * 0.75);

    ctx.globalCompositeOperation = 'destination-out';

    const tunnelCount = complexity === 'simple' ? 3 : complexity === 'normal' ? 5 : 7;
    const tunnelY: number[] = [];

    for (let i = 0; i < tunnelCount; i++) {
      const y = this.height * 0.3 + (i / tunnelCount) * this.height * 0.45;
      tunnelY.push(y);

      const tunnelHeight = 35 + this.rng.next() * 20;
      ctx.fillRect(0, y - tunnelHeight / 2, this.width, tunnelHeight);
    }

    const shaftCount = complexity === 'simple' ? 2 : complexity === 'normal' ? 4 : 6;
    for (let i = 0; i < shaftCount; i++) {
      const x = (this.width / (shaftCount + 1)) * (i + 1) + (this.rng.next() - 0.5) * 50;
      const shaftWidth = 30 + this.rng.next() * 20;

      const startTunnel = Math.floor(this.rng.next() * (tunnelY.length - 1));
      const endTunnel =
        startTunnel + 1 + Math.floor(this.rng.next() * (tunnelY.length - startTunnel - 1));

      ctx.fillRect(
        x - shaftWidth / 2,
        tunnelY[startTunnel],
        shaftWidth,
        tunnelY[Math.min(endTunnel, tunnelY.length - 1)] - tunnelY[startTunnel],
      );
    }

    const chamberCount = complexity === 'simple' ? 2 : complexity === 'normal' ? 3 : 5;
    for (let i = 0; i < chamberCount; i++) {
      const x = this.rng.next() * this.width * 0.8 + this.width * 0.1;
      const y = this.rng.next() * this.height * 0.4 + this.height * 0.35;
      const rx = 40 + this.rng.next() * 40;
      const ry = 30 + this.rng.next() * 30;
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalCompositeOperation = 'source-over';

    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, this.height * 0.25);
    ctx.lineTo(this.width, this.height * 0.25);
    ctx.stroke();

    this.addTerrainTexture(ctx, colors);
  }

  private generatePlatformsMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
  ): void {
    const rows = complexity === 'simple' ? 3 : complexity === 'normal' ? 4 : 5;
    const cols = complexity === 'simple' ? 4 : complexity === 'normal' ? 5 : 6;

    const rowSpacing = (this.height - this.waterLevel - 100) / rows;
    const colSpacing = this.width / (cols + 1);
    const platformWidth = 80;
    const platformHeight = 15;

    for (let row = 0; row < rows; row++) {
      const y = 80 + row * rowSpacing;
      const offset = (row % 2) * colSpacing * 0.5;

      for (let col = 0; col < cols; col++) {
        if (this.rng.next() > 0.15) {
          const x = colSpacing * (col + 1) + offset;
          if (x > platformWidth / 2 && x < this.width - platformWidth / 2) {
            const pw = platformWidth + (this.rng.next() - 0.5) * 40;

            ctx.fillStyle = colors.ground;
            ctx.beginPath();
            ctx.ellipse(x, y + platformHeight / 2, pw / 2, platformHeight, 0, 0, Math.PI);
            ctx.ellipse(x, y - platformHeight / 2, pw / 2, platformHeight, 0, Math.PI, Math.PI * 2);
            ctx.fill();

            ctx.strokeStyle = colors.surface;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.ellipse(
              x,
              y - platformHeight / 2,
              pw / 2,
              platformHeight / 2,
              0,
              Math.PI,
              Math.PI * 2,
            );
            ctx.stroke();
          }
        }
      }
    }

    const baseHeight = 50;
    ctx.fillStyle = colors.ground;
    ctx.fillRect(
      0,
      this.height - this.waterLevel - baseHeight,
      this.width,
      baseHeight + this.waterLevel,
    );
    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, this.height - this.waterLevel - baseHeight);
    ctx.lineTo(this.width, this.height - this.waterLevel - baseHeight);
    ctx.stroke();

    this.addTerrainTexture(ctx, colors);
  }

  private drawLargeIsland(
    ctx: TerrainDrawContext,
    cx: number,
    baseY: number,
    width: number,
    height: number,
    colors: ThemeColors,
  ): void {
    const points: { x: number; y: number }[] = [];
    const topSegments = 30;

    for (let i = 0; i <= topSegments; i++) {
      const t = i / topSegments;
      const x = cx - width / 2 + t * width;
      const noise = (this.rng.next() - 0.5) * 15;
      const hillShape = Math.sin(t * Math.PI) * height * 0.3;
      const y = baseY - height + hillShape + noise;
      points.push({ x, y });
    }

    points.push({ x: cx + width / 2, y: this.height });
    points.push({ x: cx - width / 2, y: this.height });

    ctx.fillStyle = colors.ground;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i <= topSegments; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
  }

  private smoothHeightMap(heightMap: number[], passes: number): number[] {
    let result = [...heightMap];
    for (let p = 0; p < passes; p++) {
      const smoothed: number[] = [];
      const kernelSize = 3;
      for (let i = 0; i < result.length; i++) {
        let sum = 0;
        let count = 0;
        for (let k = -kernelSize; k <= kernelSize; k++) {
          const idx = Math.max(0, Math.min(result.length - 1, i + k));
          const weight = 1 - Math.abs(k) / (kernelSize + 1);
          sum += result[idx] * weight;
          count += weight;
        }
        smoothed.push(sum / count);
      }
      result = smoothed;
    }
    return result;
  }

  private drawMainTerrain(ctx: TerrainDrawContext, heightMap: number[], colors: ThemeColors): void {
    ctx.fillStyle = colors.ground;
    ctx.beginPath();
    ctx.moveTo(0, this.height);
    for (let x = 0; x <= this.width; x++) {
      ctx.lineTo(x, heightMap[x]);
    }
    ctx.lineTo(this.width, this.height);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = 5;
    ctx.beginPath();
    for (let x = 0; x <= this.width; x++) {
      if (x === 0) {
        ctx.moveTo(x, heightMap[x]);
      } else {
        ctx.lineTo(x, heightMap[x]);
      }
    }
    ctx.stroke();
  }

  private addFloatingIslands(
    ctx: TerrainDrawContext,
    mainHeightMap: number[],
    colors: ThemeColors,
    count: number,
  ): void {
    const placer = new FeaturePlacer(this.height, this.waterLevel);
    const minY = this.height * 0.12;
    const maxY = this.height * 0.45;

    for (let i = 0; i < count; i++) {
      const sizeRoll = this.rng.next();
      let islandWidth: number, islandHeight: number, sizeCategory: 'small' | 'medium' | 'large';

      if (sizeRoll < 0.3) {
        sizeCategory = 'small';
        islandWidth = this.rng.nextInt(60, 120);
        islandHeight = this.rng.nextInt(25, 40);
      } else if (sizeRoll < 0.7) {
        sizeCategory = 'medium';
        islandWidth = this.rng.nextInt(150, 280);
        islandHeight = this.rng.nextInt(50, 80);
      } else {
        sizeCategory = 'large';
        islandWidth = this.rng.nextInt(300, 500);
        islandHeight = this.rng.nextInt(80, 140);
      }

      for (let attempt = 0; attempt < 20; attempt++) {
        const islandX = this.rng.nextInt(this.width * 0.1, this.width * 0.9);
        const mainTerrainY = mainHeightMap[Math.floor(islandX)] || this.height * 0.6;
        const maxIslandY = Math.min(maxY, mainTerrainY - 80);

        if (maxIslandY <= minY) continue;

        const islandY = this.rng.nextInt(minY, maxIslandY);

        const bounds = {
          x: islandX - islandWidth / 2,
          y: islandY - islandHeight * 0.6,
          width: islandWidth,
          height: islandHeight * 1.6,
        };

        if (placer.canPlace(bounds)) {
          placer.addFeature(bounds);
          this.drawIsland(ctx, islandX, islandY, islandWidth, islandHeight, colors, sizeCategory);
          break;
        }
      }
    }
  }

  private drawIsland(
    ctx: TerrainDrawContext,
    cx: number,
    cy: number,
    width: number,
    height: number,
    colors: ThemeColors,
    sizeCategory: 'small' | 'medium' | 'large' = 'small',
  ): void {
    ctx.fillStyle = colors.ground;
    ctx.beginPath();

    if (sizeCategory === 'small') {
      ctx.moveTo(cx - width / 2, cy);
      ctx.quadraticCurveTo(cx - width / 2, cy - height * 0.5, cx - width * 0.3, cy - height * 0.4);
      ctx.quadraticCurveTo(cx, cy - height * 0.6, cx + width * 0.3, cy - height * 0.4);
      ctx.quadraticCurveTo(cx + width / 2, cy - height * 0.5, cx + width / 2, cy);
      ctx.quadraticCurveTo(cx + width * 0.3, cy + height * 0.8, cx, cy + height * 0.6);
      ctx.quadraticCurveTo(cx - width * 0.3, cy + height * 0.8, cx - width / 2, cy);
    } else if (sizeCategory === 'medium') {
      const segments = 8;
      ctx.moveTo(cx - width / 2, cy);
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = cx - width / 2 + width * t;
        const noiseVal = simplex2D(x * 0.02, cy * 0.02) * height * 0.15;
        const baseY = cy - height * 0.5 * Math.sin(t * Math.PI);
        ctx.lineTo(x, baseY + noiseVal);
      }
      ctx.lineTo(cx + width / 2, cy);
      ctx.quadraticCurveTo(cx + width * 0.3, cy + height * 0.9, cx, cy + height * 0.7);
      ctx.quadraticCurveTo(cx - width * 0.3, cy + height * 0.9, cx - width / 2, cy);
    } else {
      const segments = 16;
      ctx.moveTo(cx - width / 2, cy);
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = cx - width / 2 + width * t;
        const noiseVal = simplex2D(x * 0.01, cy * 0.01) * height * 0.25;
        const baseY = cy - height * 0.6 * Math.sin(t * Math.PI);
        ctx.lineTo(x, baseY + noiseVal);
      }
      ctx.lineTo(cx + width / 2, cy);
      ctx.lineTo(cx + width * 0.4, cy + height * 0.5);
      ctx.lineTo(cx + width * 0.2, cy + height * 0.8);
      ctx.lineTo(cx, cy + height);
      ctx.lineTo(cx - width * 0.2, cy + height * 0.8);
      ctx.lineTo(cx - width * 0.4, cy + height * 0.5);
      ctx.lineTo(cx - width / 2, cy);
    }

    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = colors.surface;
    ctx.lineWidth = sizeCategory === 'large' ? 4 : 3;
    ctx.beginPath();
    if (sizeCategory === 'small') {
      ctx.moveTo(cx - width / 2, cy);
      ctx.quadraticCurveTo(cx - width / 2, cy - height * 0.5, cx - width * 0.3, cy - height * 0.4);
      ctx.quadraticCurveTo(cx, cy - height * 0.6, cx + width * 0.3, cy - height * 0.4);
      ctx.quadraticCurveTo(cx + width / 2, cy - height * 0.5, cx + width / 2, cy);
    } else if (sizeCategory === 'medium') {
      const segments = 8;
      ctx.moveTo(cx - width / 2, cy);
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = cx - width / 2 + width * t;
        const noiseVal = simplex2D(x * 0.02, cy * 0.02) * height * 0.15;
        const baseY = cy - height * 0.5 * Math.sin(t * Math.PI);
        ctx.lineTo(x, baseY + noiseVal);
      }
    } else {
      const segments = 16;
      ctx.moveTo(cx - width / 2, cy);
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const x = cx - width / 2 + width * t;
        const noiseVal = simplex2D(x * 0.01, cy * 0.01) * height * 0.25;
        const baseY = cy - height * 0.6 * Math.sin(t * Math.PI);
        ctx.lineTo(x, baseY + noiseVal);
      }
    }
    ctx.stroke();
  }

  private addNaturalBridges(
    ctx: TerrainDrawContext,
    heightMap: number[],
    colors: ThemeColors,
  ): void {
    const bridgeCount = this.rng.nextInt(1, 2);

    for (let i = 0; i < bridgeCount; i++) {
      const startX = this.rng.nextInt(this.width * 0.2, this.width * 0.5);
      const bridgeWidth = this.rng.nextInt(120, 200);
      const endX = startX + bridgeWidth;

      if (endX > this.width * 0.8) continue;

      const startY = heightMap[startX];
      const endY = heightMap[endX];
      const bridgeY = Math.min(startY, endY) - this.rng.nextInt(30, 60);

      if (bridgeY < this.height * 0.15) continue;

      const thickness = this.rng.nextInt(15, 25);

      ctx.fillStyle = colors.rock;
      ctx.beginPath();
      ctx.moveTo(startX, bridgeY);

      const midX = (startX + endX) / 2;
      const sag = this.rng.nextInt(8, 20);
      ctx.quadraticCurveTo(midX, bridgeY + sag, endX, bridgeY);
      ctx.lineTo(endX, bridgeY + thickness);
      ctx.quadraticCurveTo(midX, bridgeY + thickness + sag * 1.2, startX, bridgeY + thickness);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = colors.surface;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startX, bridgeY);
      ctx.quadraticCurveTo(midX, bridgeY + sag, endX, bridgeY);
      ctx.stroke();
    }
  }

  private addPillars(ctx: TerrainDrawContext, heightMap: number[], colors: ThemeColors): void {
    const pillarCount = this.rng.nextInt(2, 4);

    for (let i = 0; i < pillarCount; i++) {
      const x = this.rng.nextInt(this.width * 0.15, this.width * 0.85);
      const groundY = heightMap[x];
      const pillarHeight = this.rng.nextInt(100, 180);
      const pillarWidth = this.rng.nextInt(35, 55);
      const topY = groundY - pillarHeight;

      if (topY < this.height * 0.1) continue;

      ctx.fillStyle = colors.rock;
      ctx.beginPath();
      const taper = pillarWidth * 0.2;
      ctx.moveTo(x - pillarWidth / 2, groundY);
      ctx.lineTo(x - pillarWidth / 2 + taper, topY);
      ctx.lineTo(x + pillarWidth / 2 - taper, topY);
      ctx.lineTo(x + pillarWidth / 2, groundY);
      ctx.closePath();
      ctx.fill();

      const platformWidth = pillarWidth * 1.4;
      const platformHeight = 12;
      ctx.fillStyle = colors.ground;
      ctx.beginPath();
      ctx.ellipse(x, topY, platformWidth / 2, platformHeight, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = colors.surface;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.ellipse(x, topY - 2, platformWidth / 2, platformHeight / 2, 0, Math.PI, Math.PI * 2);
      ctx.stroke();
    }
  }

  private carveRandomCaves(ctx: TerrainDrawContext, heightMap: number[]): void {
    const minGroundY = Math.min(...heightMap);
    const caveRegionTop = minGroundY + 40;
    const caveRegionBottom = this.height - this.waterLevel - 60;
    const caveRegionHeight = caveRegionBottom - caveRegionTop;

    if (caveRegionHeight < 100) return;

    const cellSize = 4;
    const gridWidth = Math.floor(this.width / cellSize);
    const gridHeight = Math.floor(caveRegionHeight / cellSize);

    const caveOptions: CaveOptions = {
      fillPercent: 45,
      smoothPasses: 4,
      minChamberSize: 50,
      connectRegions: true,
    };

    const caveMap = generateCaveMap(gridWidth, gridHeight, this.rng, caveOptions);

    applyCaveToCanvas(ctx, caveMap, cellSize, 0, caveRegionTop);
    addFormations(ctx, caveMap, cellSize, this.rng);
  }

  private addGirders(ctx: TerrainDrawContext, heightMap: number[]): void {
    const girderCount = this.rng.nextInt(4, 7);
    const girderColor = '#666666';
    const girderBorder = '#444444';
    const girderHeight = 12;

    for (let i = 0; i < girderCount; i++) {
      const x = this.rng.nextInt(this.width * 0.1, this.width * 0.9);
      const groundY = heightMap[Math.floor(x)] || this.height * 0.6;
      const minGirderY = this.height * 0.1;
      const maxGirderY = groundY - 60;

      if (maxGirderY <= minGirderY) continue;

      const y = this.rng.nextInt(minGirderY, maxGirderY);
      const length = this.rng.nextInt(90, 150);
      const angle = (this.rng.next() - 0.5) * 30;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((angle * Math.PI) / 180);

      ctx.fillStyle = girderColor;
      ctx.fillRect(-length / 2, -girderHeight / 2, length, girderHeight);

      ctx.strokeStyle = girderBorder;
      ctx.lineWidth = 2;
      ctx.strokeRect(-length / 2, -girderHeight / 2, length, girderHeight);

      ctx.strokeStyle = '#888888';
      ctx.lineWidth = 1;
      const stripeSpacing = 20;
      for (let sx = -length / 2 + 10; sx < length / 2 - 10; sx += stripeSpacing) {
        ctx.beginPath();
        ctx.moveTo(sx, -girderHeight / 2);
        ctx.lineTo(sx + 10, girderHeight / 2);
        ctx.stroke();
      }

      ctx.restore();
    }
  }

  private addTerrainTexture(ctx: TerrainDrawContext, colors: ThemeColors): void {
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = colors.rock;
    ctx.globalAlpha = 0.2;

    for (let i = 0; i < 30; i++) {
      const x = this.rng.next() * this.width;
      const y = this.rng.next() * this.height;
      const r = 4 + this.rng.next() * 10;

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  private drawWater(ctx: TerrainDrawContext): void {
    ctx.fillStyle = '#0064C8';
    ctx.globalAlpha = 0.7;
    ctx.fillRect(0, this.height - this.waterLevel, this.width, this.waterLevel);
    ctx.globalAlpha = 1;
  }

  private generateCavernMap(
    ctx: TerrainDrawContext,
    colors: ThemeColors,
    complexity: MapComplexity,
    girders: boolean,
  ): void {
    ctx.fillStyle = colors.rock;
    ctx.fillRect(0, 0, this.width, this.height);

    const borderThickness = 60;
    const ceilingHeight = 80;
    const floorBaseY = this.height - borderThickness - this.waterLevel;
    const caveHeight = floorBaseY - ceilingHeight;

    if (caveHeight < 200) return;

    const cellSize = 4;
    const gridWidth = Math.floor((this.width - borderThickness * 2) / cellSize);
    const gridHeight = Math.floor(caveHeight / cellSize);

    const fillPercent = complexity === 'simple' ? 50 : complexity === 'normal' ? 55 : 60;
    const caveOptions: CaveOptions = {
      fillPercent,
      smoothPasses: 6,
      minChamberSize: 100,
      connectRegions: true,
    };

    const caveMap = generateCaveMap(gridWidth, gridHeight, this.rng, caveOptions);

    applyCaveToCanvas(ctx, caveMap, cellSize, borderThickness, ceilingHeight);
    addFormations(ctx, caveMap, cellSize, this.rng);

    if (girders) {
      this.addCavernGirders(ctx, caveMap, cellSize, borderThickness, ceilingHeight);
    }
  }

  private addCavernGirders(
    ctx: TerrainDrawContext,
    caveMap: boolean[][],
    cellSize: number,
    offsetX: number,
    offsetY: number,
  ): void {
    const girderCount = this.rng.nextInt(4, 7);
    const height = caveMap.length;
    const width = caveMap[0].length;

    for (let i = 0; i < girderCount; i++) {
      const x1 = this.rng.nextInt(width * 0.1, width * 0.4);
      const x2 = this.rng.nextInt(width * 0.6, width * 0.9);
      const y = this.rng.nextInt(height * 0.3, height * 0.7);

      if (!caveMap[y] || caveMap[y][x1] || caveMap[y][x2]) continue;

      ctx.strokeStyle = '#8B7355';
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.moveTo(offsetX + x1 * cellSize, offsetY + y * cellSize);
      ctx.lineTo(offsetX + x2 * cellSize, offsetY + y * cellSize);
      ctx.stroke();

      ctx.strokeStyle = '#A0826D';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(offsetX + x1 * cellSize, offsetY + y * cellSize - 3);
      ctx.lineTo(offsetX + x2 * cellSize, offsetY + y * cellSize - 3);
      ctx.stroke();
    }
  }

  private validatePlayability(
    isSolid: (x: number, y: number) => boolean,
    minSpawnPoints: number,
  ): PlayabilityResult {
    let surfacePixels = 0;

    for (let x = 0; x < this.width; x++) {
      for (let y = 0; y < this.height - this.waterLevel; y++) {
        if (isSolid(x, y) && !isSolid(x, y - 1)) {
          surfacePixels++;
        }
      }
    }

    const validSpawnPoints: { x: number; y: number }[] = [];
    const testPoints = minSpawnPoints * 3;

    for (let i = 0; i < testPoints; i++) {
      const targetX = Math.floor((this.width / (testPoints + 1)) * (i + 1));

      for (let y = 0; y < this.height - this.waterLevel; y++) {
        if (isSolid(targetX, y) && !isSolid(targetX, y - 1)) {
          const hasHeadroom = !isSolid(targetX, y - 25) && !isSolid(targetX, y - 35);

          if (hasHeadroom) {
            const minSpacing = this.width / (minSpawnPoints + 2);
            const tooClose = validSpawnPoints.some(
              (p) => Math.abs(p.x - targetX) < minSpacing * 0.4,
            );
            if (!tooClose) {
              validSpawnPoints.push({ x: targetX, y: y - 20 });
            }
          }
          break;
        }
      }
    }

    const minSurfaceArea = this.width * 0.25;
    const hasEnoughSurface = surfacePixels >= minSurfaceArea;
    const hasEnoughSpawns = validSpawnPoints.length >= minSpawnPoints;

    return {
      isPlayable: hasEnoughSurface && hasEnoughSpawns,
      surfacePixels,
      totalSolidPixels: 0,
      validSpawnPoints,
      attempts: 0,
      issues: [
        ...(hasEnoughSurface ? [] : ['Insufficient surface area for worms to stand on']),
        ...(hasEnoughSpawns
          ? []
          : [`Only ${validSpawnPoints.length} valid spawn points found, need ${minSpawnPoints}`]),
      ],
    };
  }

  generateUntilPlayable(
    ctx: TerrainDrawContext,
    isSolid: (x: number, y: number) => boolean,
    options: MapGeneratorOptions,
    minSpawnPoints: number,
    maxAttempts: number = 10,
  ): PlayabilityResult {
    let result: PlayabilityResult;
    let attempts = 0;

    do {
      this.generate(ctx, options);
      result = this.validatePlayability(isSolid, minSpawnPoints);
      attempts++;

      if (!result.isPlayable && attempts < maxAttempts) {
        console.log(
          `Map generation attempt ${attempts} failed: ${result.issues.join(', ')}. Retrying...`,
        );
      }
    } while (!result.isPlayable && attempts < maxAttempts);

    if (!result.isPlayable) {
      console.warn(
        `Failed to generate playable map after ${maxAttempts} attempts. Using last generated map.`,
      );
    }

    result.attempts = attempts;
    return result;
  }
}

export interface PlayabilityResult {
  isPlayable: boolean;
  surfacePixels: number;
  totalSolidPixels: number;
  validSpawnPoints: { x: number; y: number }[];
  /** Number of generation passes consumed (for deterministic visual replay). */
  attempts: number;
  issues: string[];
}
